/* ==========================================================================
   STYLE PHANTOM — theme.js
   Vanilla JS, custom elements, no dependencies.
   ========================================================================== */
(function () {
  'use strict';

  const SP = (window.SP = window.SP || {});
  const routes = SP.routes || {};
  const strings = SP.strings || {};

  /* ---------------- Utilities ---------------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), wait); };
  };
  const isDesktop = () => window.matchMedia('(min-width: 1100px)').matches;

  SP.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || SP.moneyFormat || '£{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, precision, thousands = ',', decimal = '.') => {
      if (isNaN(n) || n == null) return 0;
      n = (n / 100.0).toFixed(precision);
      const parts = n.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsStr = parts[1] ? decimal + parts[1] : '';
      return dollars + centsStr;
    };
    const match = format.match(placeholder);
    if (!match) return format;
    let value = '';
    switch (match[1]) {
      case 'amount': value = fmt(cents, 2); break;
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(placeholder, value);
  };

  /* Overlay + scroll lock shared by drawers / search */
  const locks = new Set();
  SP.lock = (owner) => { locks.add(owner); document.documentElement.classList.add('sp-locked'); document.body.classList.add('is-locked'); };
  SP.unlock = (owner) => {
    locks.delete(owner);
    if (!locks.size) { document.documentElement.classList.remove('sp-locked'); document.body.classList.remove('is-locked'); }
  };
  /* Safety net: never leave the page scroll-locked when nothing is actually open */
  setInterval(() => {
    if (!locks.size && !document.body.classList.contains('is-locked')) return;
    const open = document.querySelector('.spd.is-open, .sp-lightbox.is-open, .search-modal.is-open');
    if (!open) { locks.clear(); overlay.owners.clear(); document.documentElement.classList.remove('sp-locked'); document.body.classList.remove('is-locked'); if (overlay.el) { overlay.el.classList.remove('is-visible'); overlay.el.hidden = true; } }
  }, 1200);

  const overlay = {
    el: null,
    owners: new Set(),
    init() {
      this.el = $('[data-overlay]');
      if (this.el) this.el.addEventListener('click', () => document.dispatchEvent(new CustomEvent('sp:overlay-click')));
    },
    show(owner) {
      this.owners.add(owner);
      SP.lock('overlay:' + owner);
      if (!this.el) return;
      this.el.hidden = false;
      requestAnimationFrame(() => this.el.classList.add('is-visible'));
    },
    hide(owner) {
      this.owners.delete(owner);
      SP.unlock('overlay:' + owner);
      if (this.owners.size) return;
      if (!this.el) return;
      this.el.classList.remove('is-visible');
      setTimeout(() => { if (!this.owners.size) this.el.hidden = true; }, 450);
    }
  };

  /* Toast */
  SP.toast = function (message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(el._t);
    el._t = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => (el.hidden = true), 400);
    }, 2600);
  };

  /* Focus trap helper */
  const focusable = (ctx) => $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', ctx).filter((el) => el.offsetParent !== null);

  /* ---------------- Cart API ---------------- */
  SP.cart = {
    async get() {
      const res = await fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } });
      return res.json();
    },
    async add(items) {
      const body = { items: Array.isArray(items) ? items : [items] };
      if (SP.cartType === 'drawer') body.sections = 'cart-drawer';
      const res = await fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.error);
      await SP.cart.afterAdd(data);
      return data;
    },
    async change(line, quantity) {
      const body = { line, quantity };
      if (SP.cartType === 'drawer') body.sections = 'cart-drawer';
      const res = await fetch(routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.error);
      if (data.sections && data.sections['cart-drawer']) SP.cart.renderDrawer(data.sections['cart-drawer']);
      SP.cart.updateCount(data.item_count);
      document.dispatchEvent(new CustomEvent('sp:cart-updated', { detail: data }));
      return data;
    },
    async afterAdd(data) {
      document.dispatchEvent(new CustomEvent('sp:cart-added', { detail: data }));
      if (SP.cartType === 'upcart') {
        try { if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart(); } catch (e) {}
        try { if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart(); } catch (e) {}
        SP.cart.refreshCount();
        return;
      }
      if (SP.cartType === 'drawer') {
        if (data.sections && data.sections['cart-drawer']) SP.cart.renderDrawer(data.sections['cart-drawer']);
        else await SP.cart.refreshDrawer();
        SP.cart.refreshCount();
        const drawer = document.getElementById('CartDrawer');
        if (drawer) drawer.open();
        return;
      }
      SP.cart.refreshCount();
      SP.toast(strings.added);
    },
    async refreshDrawer() {
      const res = await fetch(routes.root.replace(/\/$/, '') + '/?sections=cart-drawer');
      const json = await res.json();
      if (json['cart-drawer']) SP.cart.renderDrawer(json['cart-drawer']);
    },
    renderDrawer(html) {
      const drawer = document.getElementById('CartDrawer');
      if (!drawer) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer-inner]');
      const current = drawer.querySelector('[data-cart-drawer-inner]');
      if (fresh && current) current.replaceWith(fresh);
      drawer.dispatchEvent(new CustomEvent('sp:drawer-rendered'));
    },
    async refreshCount() {
      try {
        const cart = await SP.cart.get();
        SP.cart.updateCount(cart.item_count);
      } catch (e) {}
    },
    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => (el.textContent = count));
      $$('[data-cart-bubble]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
    }
  };

  /* Keep the header count in sync with ANY cart mutation (incl. UpCart / other apps) */
  (function patchFetch() {
    if (!window.fetch || window.fetch._spPatched) return;
    const original = window.fetch;
    const refresh = debounce(() => SP.cart.refreshCount(), 350);
    const patched = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const p = original.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) p.then(refresh).catch(() => {});
      return p;
    };
    patched._spPatched = true;
    window.fetch = patched;
  })();

  /* Cart trigger behaviour */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-trigger]');
    if (!trigger) return;
    if (SP.cartType === 'upcart' && typeof window.upcartOpenCart === 'function') {
      e.preventDefault();
      window.upcartOpenCart();
    } else if (SP.cartType === 'drawer') {
      const drawer = document.getElementById('CartDrawer');
      if (drawer) { e.preventDefault(); drawer.open(); }
    }
  });

  /* ---------------- Drawer ---------------- */
  class SpDrawer extends HTMLElement {
    connectedCallback() {
      // Drawers marked data-portal are moved to <body> so no ancestor (sticky/transform/overflow) can hide or trap them.
      if (this.hasAttribute('data-portal') && this.parentElement !== document.body) {
        document.querySelectorAll(`body > [id="${this.id}"]`).forEach((el) => { if (el !== this) el.remove(); });
        document.body.appendChild(this);
        return;
      }
      if (this._bound) return;
      this._bound = true;
      this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
      this.onOverlay = () => this.close();
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
    }
    open(trigger) {
      if (!this._bound) this.connectedCallback();
      if (this.classList.contains('is-open')) return;
      this._trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      overlay.show(this.id);
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('sp:overlay-click', this.onOverlay);
      if (this._trigger && this._trigger.setAttribute) this._trigger.setAttribute('aria-expanded', 'true');
      setTimeout(() => { const f = focusable(this)[0]; (f || this).focus({ preventScroll: true }); }, 80);
      this.dispatchEvent(new CustomEvent('sp:open', { detail: { trigger: this._trigger } }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      overlay.hide(this.id);
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('sp:overlay-click', this.onOverlay);
      if (this._trigger && this._trigger.setAttribute) {
        this._trigger.setAttribute('aria-expanded', 'false');
        try { this._trigger.focus({ preventScroll: true }); } catch (e) {}
      }
    }
  }
  customElements.define('sp-drawer', SpDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer) return;
    e.preventDefault();
    if (opener.dataset.guideTab) SP.setGuideTab(drawer, opener.dataset.guideTab);
    if (typeof drawer.open === 'function') drawer.open(opener);
    else { drawer.classList.add('is-open'); overlay.show(drawer.id); }
  });

  /* Size guide tabs (Blazer / Trousers) */
  SP.setGuideTab = (root, tab) => {
    const btns = $$('[data-guide-tab-btn]', root);
    const panels = $$('[data-guide-panel]', root);
    if (!panels.length) return;
    if (!panels.some((p) => p.dataset.guidePanel === tab)) tab = panels[0].dataset.guidePanel;
    btns.forEach((b) => b.setAttribute('aria-selected', String(b.dataset.guideTabBtn === tab)));
    panels.forEach((p) => { p.hidden = p.dataset.guidePanel !== tab; });
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-guide-tab-btn]');
    if (b) SP.setGuideTab(b.closest('[data-size-guide]'), b.dataset.guideTabBtn);
  });

  /* ---------------- Header ---------------- */
  class SpHeader extends HTMLElement {
    connectedCallback() {
      this.header = $('[data-header]', this);
      this.section = this.closest('.sp-header-section') || this.parentElement;
      this.transparent = this.hasAttribute('data-transparent');
      this.sticky = this.dataset.sticky;
      this.lastY = window.scrollY;
      if (this.sticky === 'none' && this.section) this.section.classList.add('is-static');
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.initMega();
    }
    setSolid(force) {
      if (!this.transparent) return;
      const solid = force || window.scrollY > 40 || this.megaOpen;
      this.header.classList.toggle('is-solid', !!solid);
    }
    onScroll() {
      const y = window.scrollY;
      this.setSolid();
      if (this.sticky === 'scroll-up' && this.section) {
        const hide = y > this.lastY && y > 240 && !this.megaOpen;
        this.section.classList.toggle('is-hidden', hide);
        document.body.classList.toggle('header-hidden', hide);
      }
      this.lastY = y;
    }
    initMega() {
      const items = $$('[data-mega]', this);
      let closeTimer;
      const openItem = (item) => {
        clearTimeout(closeTimer);
        items.forEach((i) => { if (i !== item) { i.classList.remove('is-open'); const a = $('.main-nav__link', i); if (a) a.setAttribute('aria-expanded', 'false'); } });
        item.classList.add('is-open');
        const link = $('.main-nav__link', item);
        if (link) link.setAttribute('aria-expanded', 'true');
        this.megaOpen = true;
        this.setSolid(true);
      };
      const closeAll = () => {
        items.forEach((i) => { i.classList.remove('is-open'); const a = $('.main-nav__link', i); if (a) a.setAttribute('aria-expanded', 'false'); });
        this.megaOpen = false;
        this.setSolid();
      };
      items.forEach((item) => {
        item.addEventListener('mouseenter', () => { if (isDesktop()) openItem(item); });
        item.addEventListener('mouseleave', () => { closeTimer = setTimeout(closeAll, 140); });
        item.addEventListener('focusin', () => openItem(item));
        item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeAll(); const a = $('.main-nav__link', item); if (a) a.focus(); } });
      });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) closeAll(); });
    }
  }
  customElements.define('sp-header', SpHeader);

  /* ---------------- Announcement ---------------- */
  class SpAnnouncement extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-announcement-item]', this);
      if (this.items.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.start();
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.start());
    }
    start() {
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }, this.interval);
    }
  }
  customElements.define('sp-announcement', SpAnnouncement);

  /* ---------------- Search ---------------- */
  class SpSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-search-input]', this);
      this.results = $('[data-search-results]', this);
      this.defaults = $('[data-search-default]', this);
      this.predictive = this.dataset.predictive === 'true';
      this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
      this.onOverlay = () => this.close();
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-open]')) { e.preventDefault(); this.open(); }
      });
      this.addEventListener('click', (e) => { if (e.target.closest('[data-search-close]')) this.close(); });
      if (this.predictive && this.input) this.input.addEventListener('input', debounce(() => this.search(), 260));
    }
    open() {
      this.classList.add('is-open');
      overlay.show('search');
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('sp:overlay-click', this.onOverlay);
      setTimeout(() => this.input && this.input.focus(), 120);
    }
    close() {
      this.classList.remove('is-open');
      overlay.hide('search');
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('sp:overlay-click', this.onOverlay);
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; this.defaults.hidden = false; return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,query,page&resources[limit]=10&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: this.controller.signal });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-predictive-content]');
        this.results.innerHTML = content ? content.outerHTML : '';
        this.defaults.hidden = true;
      } catch (e) { /* aborted */ }
    }
  }
  customElements.define('sp-search', SpSearch);

  /* ---------------- Cart drawer & cart page items ---------------- */
  class SpCartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-btn]');
        const remove = e.target.closest('[data-remove-line]');
        if (btn) {
          e.preventDefault();
          const line = parseInt(btn.dataset.line, 10);
          const input = $(`input[data-line="${line}"]`, this);
          const qty = Math.max(0, parseInt(input.value, 10) + (btn.dataset.qtyBtn === 'plus' ? 1 : -1));
          this.update(line, qty);
        }
        if (remove) { e.preventDefault(); this.update(parseInt(remove.dataset.removeLine, 10), 0); }
      });
      this.addEventListener('change', debounce((e) => {
        const input = e.target.closest('input[data-line]');
        if (input) this.update(parseInt(input.dataset.line, 10), parseInt(input.value, 10) || 0);
      }, 300));
      const note = $('[data-cart-note]', this.closest('[data-cart-root]') || document);
      if (note && !note._bound) {
        note._bound = true;
        note.addEventListener('input', debounce(() => {
          fetch(routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
        }, 500));
      }
    }
    async update(line, quantity) {
      this.classList.add('is-loading');
      try {
        await SP.cart.change(line, quantity);
        if (this.dataset.page === 'true') await this.refreshPage();
      } catch (err) {
        SP.toast(err.message);
      }
      this.classList.remove('is-loading');
    }
    async refreshPage() {
      const sectionId = this.dataset.sectionId;
      const res = await fetch(`${routes.cart}?section_id=${sectionId}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-root]');
      const current = this.closest('[data-cart-root]');
      if (fresh && current) current.replaceWith(fresh);
    }
  }
  customElements.define('sp-cart-items', SpCartItems);

  class SpCartDrawer extends SpDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('sp:open', () => this.loadUpsells());
      this.addEventListener('sp:drawer-rendered', () => { this._upsellsFor = null; if (this.classList.contains('is-open')) this.loadUpsells(); });
    }
    async loadUpsells() {
      const wrap = $('[data-cart-upsell]', this);
      if (!wrap) return;
      const pid = wrap.dataset.productId;
      if (!pid || this._upsellsFor === pid) return;
      this._upsellsFor = pid;
      try {
        const res = await fetch(`${routes.recommendations}?product_id=${pid}&limit=6&section_id=cart-upsell&intent=related`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-upsell-inner]');
        if (inner && inner.children.length) { wrap.innerHTML = inner.outerHTML; wrap.hidden = false; }
      } catch (e) {}
    }
  }
  customElements.define('sp-cart-drawer', SpCartDrawer);

  /* Generic add-to-cart for simple buttons: <button data-add-variant="123"> */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-variant]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    try { await SP.cart.add({ id: parseInt(btn.dataset.addVariant, 10), quantity: 1 }); }
    catch (err) { SP.toast(err.message); }
    btn.classList.remove('is-loading');
  });

  /* ---------------- Product cards: swatches + quick add ---------------- */
  document.addEventListener('click', async (e) => {
    const swatch = e.target.closest('.card__swatch');
    if (swatch) {
      e.preventDefault();
      activateSwatch(swatch);
      return;
    }
    const toggle = e.target.closest('[data-quick-toggle]');
    if (toggle) {
      e.preventDefault();
      const panel = $('[data-quick-add]', toggle.closest('[data-card]'));
      if (panel) panel.classList.toggle('is-open');
      return;
    }
    const sizeBtn = e.target.closest('.quick-add__size');
    if (sizeBtn) {
      e.preventDefault();
      const card = sizeBtn.closest('[data-card]');
      const json = $('[data-card-json]', card);
      if (!json) return;
      const data = JSON.parse(json.textContent);
      const activeSwatch = $('.card__swatch.is-active', card);
      const color = activeSwatch ? activeSwatch.dataset.color : null;
      const size = sizeBtn.dataset.size;
      const variant = data.variants.find((v) => v.options[data.sizeIndex - 1] === size && (!color || !data.colorIndex || v.options[data.colorIndex - 1] === color));
      if (!variant || !variant.available) { SP.toast(strings.soldOut); return; }
      sizeBtn.classList.add('is-loading');
      try {
        await SP.cart.add({ id: variant.id, quantity: 1 });
        const panel = $('[data-quick-add]', card);
        if (panel) panel.classList.remove('is-open');
      } catch (err) { SP.toast(err.message); }
      sizeBtn.classList.remove('is-loading');
    }
  });
  document.addEventListener('mouseover', (e) => {
    const swatch = e.target.closest('.card__swatch');
    if (swatch && isDesktop()) activateSwatch(swatch);
  });
  function activateSwatch(swatch) {
    const card = swatch.closest('[data-card]');
    if (!card || swatch.classList.contains('is-active')) return;
    $$('.card__swatch', card).forEach((s) => s.classList.toggle('is-active', s === swatch));
    const img = $('.card__img--main', card);
    if (img && swatch.dataset.src) {
      img.srcset = swatch.dataset.srcset || '';
      img.src = swatch.dataset.src;
    }
    if (swatch.dataset.url) $$('a[href]', card).forEach((a) => { if (a.closest('.card__title') || a.classList.contains('card__media')) a.href = swatch.dataset.url; });
    refreshQuickAvailability(card, swatch.dataset.color);
  }
  function refreshQuickAvailability(card, color) {
    const json = $('[data-card-json]', card);
    if (!json) return;
    const data = JSON.parse(json.textContent);
    $$('.quick-add__size', card).forEach((btn) => {
      const v = data.variants.find((x) => x.options[data.sizeIndex - 1] === btn.dataset.size && (!data.colorIndex || !color || x.options[data.colorIndex - 1] === color));
      btn.disabled = !v || !v.available;
    });
  }

  /* ---------------- Slider ---------------- */
  class SpSlider extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-slider-track]', this);
      if (!this.track) return;
      this.prev = $('[data-slider-prev]', this);
      this.next = $('[data-slider-next]', this);
      this.bar = $('[data-slider-bar]', this);
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      window.addEventListener('resize', debounce(() => this.update(), 150));
      this.update();
    }
    go(dir) {
      const slide = this.track.firstElementChild;
      const step = slide ? slide.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth;
      this.track.scrollBy({ left: dir * step * (isDesktop() ? 2 : 1), behavior: 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const ratio = max > 0 ? this.track.scrollLeft / max : 0;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
      if (this.bar) {
        const visible = this.track.clientWidth / this.track.scrollWidth;
        this.bar.style.width = Math.max(visible * 100, 8) + '%';
        this.bar.style.transform = `translateX(${ratio * (1 / Math.max(visible, 0.08) - 1) * 100}%)`;
      }
    }
  }
  customElements.define('sp-slider', SpSlider);

  /* ---------------- Product page ---------------- */
  class SpProduct extends HTMLElement {
    connectedCallback() {
      const json = $('[data-product-json]', this);
      if (!json || this._init) return;
      this._init = true;
      this.data = JSON.parse(json.textContent);
      this.sizeIndexes = (this.data.sizeIndexes && this.data.sizeIndexes.length) ? this.data.sizeIndexes : (this.data.sizeIndex ? [this.data.sizeIndex] : []);
      this.chosen = new Set();
      if (this.data.preselectSize) this.sizeIndexes.forEach((i) => this.chosen.add(i));
      this.buy = $('[data-buy]', this);
      this.idInputs = $$('[data-variant-id]', this);
      this.atc = $('[data-atc]', this);
      this.priceEl = $('[data-product-price]', this);
      this.gallery = $('sp-gallery', this) || document.querySelector('sp-gallery');
      this.sticky = $('[data-sticky-atc]', this) || document.querySelector('[data-sticky-atc]');

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input]')) {
          const idx = parseInt(e.target.dataset.index, 10);
          if (this.sizeIndexes.includes(idx)) this.chosen.add(idx);
          this.onOptionChange();
        }
      });
      if (this.atc) this.atc.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.handleAdd(); });
      this.initSticky();
      this.onOptionChange(true);
      this.initDelivery();
      this.initReviewsLink();
      this.initStickyColumn();
      this.trackRecentlyViewed();
    }
    allSizesChosen() { return this.sizeIndexes.every((i) => this.chosen.has(i)); }
    firstMissingSize() { return this.sizeIndexes.find((i) => !this.chosen.has(i)); }
    selectedOptions() {
      return this.data.options.map((_, i) => {
        const select = $(`select[data-option-input][data-index="${i + 1}"]`, this);
        if (select) return select.value;
        const checked = $(`[data-option-input][data-index="${i + 1}"]:checked`, this);
        return checked ? checked.value : null;
      });
    }
    onOptionChange(initial) {
      const selected = this.selectedOptions();
      const matches = (v) => v.options.every((o, i) => selected[i] == null || o === selected[i]);
      const exact = this.data.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      const display = exact || this.data.variants.find((v) => v.available && matches(v)) || this.data.variants.find(matches);
      this.variant = exact;
      this.updateLabels(selected);
      this.markAvailability(selected);
      this.updateButtons();
      if (display) this.updatePrice(display);
      // The variant id is only filled in once every option (incl. all sizes) has been chosen.
      const ready = exact && this.allSizesChosen();
      this.idInputs.forEach((i) => { i.value = ready ? exact.id : ''; });
      if (exact) {
        $$('form[id^="InstallmentsForm"] input[name="id"]', this).forEach((i) => { i.value = exact.id; i.dispatchEvent(new Event('change', { bubbles: true })); });
        if (!initial) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', exact.id);
          window.history.replaceState({}, '', url.toString());
        }
        if (this.stickySelect) this.stickySelect.value = String(exact.id);
      }
      const color = this.data.colorIndex ? selected[this.data.colorIndex - 1] : null;
      const colorChanged = this.data.colorIndex && this.lastColor !== undefined && this.lastColor !== color;
      if (!initial && display && display.featured_media_id && this.gallery && (colorChanged || !this.data.colorIndex)) {
        this.gallery.showMedia(display.featured_media_id, true);
      }
      this.lastColor = color;
      this.dispatchEvent(new CustomEvent('sp:variant-change', { detail: exact || display, bubbles: true }));
    }
    updateLabels(selected) {
      selected.forEach((val, i) => {
        const label = $(`[data-option-selected="${i + 1}"]`, this);
        if (!label) return;
        const input = $(`[data-option-input][data-index="${i + 1}"]:checked`, this);
        label.textContent = input && input.dataset.label ? input.dataset.label : (val || '');
      });
    }
    markAvailability(selected) {
      const count = this.data.options.length;
      const order = [];
      if (this.data.colorIndex) order.push(this.data.colorIndex - 1);
      for (let i = 0; i < count; i++) if (!order.includes(i)) order.push(i);
      order.forEach((idx, p) => {
        const higher = order.slice(0, p);
        $$(`input[data-option-input][data-index="${idx + 1}"]`, this).forEach((input) => {
          const ok = this.data.variants.some((v) => v.available && v.options[idx] === input.value && higher.every((h) => selected[h] == null || v.options[h] === selected[h]));
          input.classList.toggle('is-unavailable', !ok);
        });
      });
    }
    updateButtons() {
      const v = this.variant;
      let label = strings.addToBag;
      let disabled = false;
      const needSize = !this.allSizesChosen();
      if (needSize) label = this.sizeIndexes.length > 1 && this.chosen.size === 0 ? (strings.selectSizes || strings.selectSize) : strings.selectSize;
      else if (!v) { label = strings.unavailable; disabled = true; }
      else if (!v.available) { label = strings.soldOut; disabled = true; }
      [this.atc, this.stickyBtn].forEach((btn) => {
        if (!btn) return;
        btn.disabled = disabled;
        const lbl = btn.querySelector('[data-atc-label]') || btn;
        lbl.textContent = label;
      });
      const dyn = $('[data-dynamic-checkout]', this);
      if (dyn) dyn.style.display = needSize || disabled ? 'none' : '';
    }
    updatePrice(v) {
      if (!this.priceEl) return;
      const sale = v.compare_at_price && v.compare_at_price > v.price;
      this.priceEl.innerHTML = `<span class="price${sale ? ' price--on-sale' : ''}"><span class="price__current">${SP.formatMoney(v.price)}</span>${sale ? `<s class="price__compare">${SP.formatMoney(v.compare_at_price)}</s>` : ''}</span>`;
      if (this.stickyPrice) this.stickyPrice.innerHTML = SP.formatMoney(v.price);
    }
    nudgeSize() {
      const missing = this.firstMissingSize();
      const group = missing && $(`[data-option-group="${missing}"]`, this);
      if (!group) return;
      const top = group.getBoundingClientRect().top + window.scrollY - (window.innerHeight / 3);
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      group.classList.remove('is-nudged');
      void group.offsetWidth;
      group.classList.add('is-nudged');
    }
    bounce() {
      [this.atc, this.stickyBtn].forEach((btn) => {
        if (!btn) return;
        btn.classList.remove('is-bouncing');
        void btn.offsetWidth;
        btn.classList.add('is-bouncing');
      });
    }
    async handleAdd() {
      if (this._adding) return;
      // Never add to bag until every option (colour and every size) has been chosen.
      if (!this.allSizesChosen() || !this.variant) {
        this.bounce();
        this.nudgeSize();
        return;
      }
      if (!this.variant.available) return;
      this._adding = true;
      const btn = this.atc;
      btn.classList.add('is-loading');
      const errorEl = $('[data-form-error]', this);
      if (errorEl) errorEl.hidden = true;
      try {
        const item = { id: this.variant.id, quantity: 1 };
        const props = {};
        $$('[data-line-property]', this).forEach((f) => { const m = f.name.match(/^properties\[(.+)\]$/); if (m && f.value.trim()) props[m[1]] = f.value.trim(); });
        if (Object.keys(props).length) item.properties = props;
        await SP.cart.add(item);
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
        else SP.toast(err.message);
      }
      btn.classList.remove('is-loading');
      this._adding = false;
    }
    initSticky() {
      if (!this.sticky || !this.atc) return;
      this.stickyBtn = $('[data-sticky-btn]', this.sticky);
      this.stickySelect = $('[data-sticky-select]', this.sticky);
      this.stickyPrice = $('[data-sticky-price]', this.sticky);
      const footer = document.querySelector('.footer');
      let atcVisible = true; let footerVisible = false;
      const update = () => {
        const show = !atcVisible && !footerVisible && window.scrollY > 300;
        this.sticky.classList.toggle('is-visible', show);
      };
      new IntersectionObserver(([en]) => { atcVisible = en.isIntersecting || en.boundingClientRect.top > 0; update(); }).observe(this.atc);
      if (footer) new IntersectionObserver(([en]) => { footerVisible = en.isIntersecting; update(); }).observe(footer);
      if (this.stickySelect) {
        this.stickySelect.addEventListener('change', () => {
          const v = this.data.variants.find((x) => String(x.id) === this.stickySelect.value);
          if (!v) return;
          v.options.forEach((val, i) => {
            const input = $$(`[data-option-input][data-index="${i + 1}"]`, this).find((el) => el.value === val);
            if (input) input.checked = true;
          });
          this.sizeIndexes.forEach((i) => this.chosen.add(i));
          this.onOptionChange();
        });
      }
      if (this.stickyBtn) {
        this.stickyBtn.addEventListener('click', () => {
          this.handleAdd();
        });
      }
      this.updateButtons();
    }
    /* Delivery estimate + live cut-off countdown in the store's time zone (default America/New_York) */
    initDelivery() {
      const el = $('[data-delivery]', this);
      const d = SP.delivery;
      if (!el || !d || !d.enabled) return;
      const tz = d.timezone || 'America/New_York';
      const cutText = $('[data-cutoff-text]', el);
      const dateText = $('[data-delivery-text]', el);
      const lang = document.documentElement.lang || 'en';
      const partsIn = (date) => {
        const p = {};
        new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', weekday: 'short' })
          .formatToParts(date).forEach((x) => { p[x.type] = x.value; });
        return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour, min: +p.minute, s: +p.second, wd: p.weekday };
      };
      // Calendar arithmetic on a UTC-noon date that represents the store-local calendar day.
      const isWeekend = (dt) => { const g = dt.getUTCDay(); return g === 0 || g === 6; };
      const addBusiness = (dt, n) => { const r = new Date(dt); let a = 0; while (a < n) { r.setUTCDate(r.getUTCDate() + 1); if (!isWeekend(r)) a++; } return r; };
      const fmt = (dt) => dt.toLocaleDateString(lang, { timeZone: 'UTC', month: 'short', day: 'numeric' });
      const render = () => {
        const now = partsIn(new Date());
        const today = new Date(Date.UTC(now.y, now.m - 1, now.d, 12));
        const secsNow = now.h * 3600 + now.min * 60 + now.s;
        const cutoffSecs = d.cutoff * 3600;
        const shipsToday = !isWeekend(today) && secsNow < cutoffSecs;
        const dispatch = shipsToday ? today : addBusiness(today, 1);
        // Delivery window: business days counted from the processing day (orders after cut-off roll to the next business day).
        const from = addBusiness(dispatch, d.min);
        const to = addBusiness(dispatch, d.max);
        if (dateText) dateText.innerHTML = strings.deliveryEstimate.replace('[from]', fmt(from)).replace('[to]', fmt(to));
        if (cutText) {
          if (shipsToday) {
            const left = cutoffSecs - secsNow;
            const hh = Math.floor(left / 3600); const mm = Math.floor((left % 3600) / 60); const ss = left % 60;
            const pad = (n) => String(n).padStart(2, '0');
            const time = `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
            cutText.innerHTML = strings.dispatchToday.replace('[time]', `<span class="delivery-est__timer">${time}</span>`);
          } else {
            const day = dispatch.toLocaleDateString(lang, { timeZone: 'UTC', weekday: 'long' });
            el.classList.add('is-next');
            cutText.innerHTML = (strings.dispatchNext || '').replace('[day]', day);
          }
        }
      };
      render();
      el.hidden = false;
      clearInterval(this._deliveryTimer);
      this._deliveryTimer = setInterval(render, 1000);
    }
    /* Star rating link: scroll so the reviews heading sits just below the header */
    initReviewsLink() {
      $$('[data-scroll-reviews]', this).forEach((link) => {
        link.addEventListener('click', (e) => {
          const target = document.getElementById('Reviews');
          if (!target) return;
          e.preventDefault();
          const go = () => {
            const header = document.querySelector('.sp-header-section');
            const offset = (header && !header.classList.contains('is-static') ? header.offsetHeight : 0) + 16;
            const top = target.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          };
          go();
          setTimeout(go, 650); // re-align once lazy content above has settled
        });
      });
    }
    /* Sticky info column that never traps content taller than the viewport */
    initStickyColumn() {
      const col = $('.pdp__info-sticky', this);
      if (!col || !('ResizeObserver' in window)) return;
      const update = () => {
        if (!isDesktop()) { col.style.removeProperty('--sticky-top'); return; }
        const header = document.querySelector('.sp-header-section');
        const headerH = header ? header.offsetHeight : 0;
        const avail = window.innerHeight - headerH - 24;
        const h = col.offsetHeight;
        const top = h <= avail ? headerH + 8 : window.innerHeight - h - 24;
        col.style.setProperty('--sticky-top', `${Math.round(top)}px`);
      };
      new ResizeObserver(update).observe(col);
      window.addEventListener('resize', debounce(update, 120));
      update();
    }
    trackRecentlyViewed() {
      try {
        const key = 'sp-recent';
        const list = JSON.parse(localStorage.getItem(key) || '[]').filter((h) => h !== this.data.handle);
        list.unshift(this.data.handle);
        localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
      } catch (e) {}
    }
  }
  customElements.define('sp-product', SpProduct);

  /* ---------------- Gallery + lightbox ---------------- */
  class SpGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.counter = $('[data-gallery-current]', this);
      this.dots = $$('[data-gallery-dot]', this);
      this.lightbox = document.getElementById(this.dataset.lightbox);
      if (this.lightbox && this.lightbox.parentElement !== document.body) {
        document.querySelectorAll(`body > [id="${this.lightbox.id}"]`).forEach((el) => { if (el !== this.lightbox) el.remove(); });
        document.body.appendChild(this.lightbox);
      }
      if (this.track) this.track.addEventListener('scroll', debounce(() => this.onScroll(), 40), { passive: true });
      this.addEventListener('click', (e) => {
        const item = e.target.closest('[data-media-id]');
        if (item && item.dataset.mediaType === 'image') this.openLightbox(item.dataset.mediaId);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const item = e.target.closest('[data-media-id]');
        if (item && item.dataset.mediaType === 'image') { e.preventDefault(); this.openLightbox(item.dataset.mediaId); }
      });
      if (this.lightbox) {
        this.lightbox.addEventListener('click', (e) => { if (e.target.closest('[data-lightbox-close]') || e.target.tagName === 'IMG') this.closeLightbox(); });
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.lightbox.classList.contains('is-open')) this.closeLightbox(); });
      }
    }
    items() { return $$('[data-media-id]', this); }
    onScroll() {
      if (isDesktop()) return;
      const idx = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (this.counter) this.counter.textContent = idx + 1;
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
    }
    showMedia(mediaId, animate) {
      const target = this.items().find((el) => el.dataset.mediaId === String(mediaId));
      if (!target) return;
      if (isDesktop()) {
        if (this.track.firstElementChild !== target) {
          this.track.prepend(target);
          this.dots.forEach((d, i) => d.classList.toggle('is-active', i === 0));
        }
        if (animate) {
          target.animate([{ opacity: 0.2 }, { opacity: 1 }], { duration: 600, easing: 'cubic-bezier(.22,.61,.36,1)' });
          const top = this.getBoundingClientRect().top + window.scrollY - 100;
          if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
        }
      } else {
        this.track.scrollTo({ left: target.offsetLeft, behavior: animate ? 'smooth' : 'auto' });
      }
    }
    openLightbox(mediaId) {
      if (!this.lightbox) return;
      this.lightbox.classList.add('is-open');
      SP.lock('lightbox');
      const img = this.lightbox.querySelector(`[data-lightbox-id="${mediaId}"]`);
      $$('img[data-lazy-src]', this.lightbox).forEach((el) => { el.src = el.dataset.lazySrc; el.removeAttribute('data-lazy-src'); });
      requestAnimationFrame(() => { if (img) this.lightbox.scrollTop = img.offsetTop; });
      const close = $('[data-lightbox-close]', this.lightbox);
      if (close) close.focus({ preventScroll: true });
    }
    closeLightbox() {
      this.lightbox.classList.remove('is-open');
      SP.unlock('lightbox');
    }
  }
  customElements.define('sp-gallery', SpGallery);

  /* ---------------- Product recommendations ---------------- */
  class SpRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector(`sp-recommendations[data-section="${this.dataset.section}"]`);
          if (fresh && fresh.querySelector('[data-card]')) {
            this.innerHTML = fresh.innerHTML;
            this.hidden = false;
            initReveal(this);
          } else if (this.dataset.hideEmpty === 'true') {
            const section = this.closest('.shopify-section');
            if (section) section.hidden = true;
          }
        } catch (e) {}
      };
      new IntersectionObserver((entries, obs) => { if (entries[0].isIntersecting) { obs.disconnect(); load(); } }, { rootMargin: '0px 0px 600px 0px' }).observe(this);
    }
  }
  customElements.define('sp-recommendations', SpRecommendations);

  /* ---------------- Recently viewed ---------------- */
  class SpRecent extends HTMLElement {
    async connectedCallback() {
      let list = [];
      try { list = JSON.parse(localStorage.getItem('sp-recent') || '[]'); } catch (e) {}
      const current = this.dataset.current;
      list = list.filter((h) => h !== current).slice(0, parseInt(this.dataset.limit || '4', 10));
      if (!list.length) return;
      const cards = await Promise.all(list.map(async (h) => {
        try {
          const res = await fetch(`${routes.root.replace(/\/$/, '')}/products/${h}?view=card`);
          if (!res.ok) return '';
          return await res.text();
        } catch (e) { return ''; }
      }));
      const grid = $('[data-recent-grid]', this);
      const html = cards.filter(Boolean).join('');
      if (!html || !grid) return;
      grid.innerHTML = html;
      this.closest('.shopify-section').hidden = false;
      this.hidden = false;
    }
  }
  customElements.define('sp-recent', SpRecent);

  /* ---------------- Collection: filters, sort, load more, density ---------------- */
  class SpCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.bindForm();
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-filter-link]');
        if (remove) { e.preventDefault(); this.render(remove.getAttribute('href')); }
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); }
        const dens = e.target.closest('[data-density]');
        if (dens) this.setDensity(dens);
      });
      this.restoreDensity();
      window.addEventListener('popstate', () => this.render(window.location.href, false));
    }
    bindForm() {
      this.form = document.getElementById('FacetsForm-' + this.sectionId);
      if (!this.form || this.form._bound) return;
      this.form._bound = true;
      this.form.addEventListener('input', debounce(() => this.onFormChange(), 450));
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.onFormChange(); const d = this.form.closest('sp-drawer'); if (d) d.close(); });
    }
    onFormChange() {
      const params = new URLSearchParams(new FormData(this.form));
      for (const [k, v] of Array.from(params.entries())) { if (v === '') params.delete(k); }
      this.render(`${window.location.pathname}?${params.toString()}`);
    }
    async render(url, push = true) {
      const u = new URL(url, window.location.origin);
      u.searchParams.delete('page');
      const grid = $('[data-collection-grid-wrap]', this);
      if (grid) grid.classList.add('is-loading');
      u.searchParams.set('section_id', this.sectionId);
      try {
        const res = await fetch(u.toString());
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const openIds = $$('[data-facets-body] details[data-facet-id]').map((d) => [d.dataset.facetId, d.open]);
        ['[data-collection-grid-wrap]', '[data-toolbar-count]', '[data-active-filters]', '[data-facets-body]', '[data-facets-footer]'].forEach((sel) => {
          const fresh = doc.querySelector(sel);
          const current = document.querySelector(`#shopify-section-${this.sectionId} ${sel}`) || $(sel, this);
          if (fresh && current) current.replaceWith(fresh);
        });
        openIds.forEach(([id, open]) => { const d = document.querySelector(`[data-facets-body] details[data-facet-id="${CSS.escape(id)}"]`); if (d) d.open = open; });
        u.searchParams.delete('section_id');
        if (push) window.history.pushState({}, '', u.toString());
        this.restoreDensity();
        initReveal(this);
      } catch (e) { SP.toast(strings.error); }
      const g = $('[data-collection-grid-wrap]', this);
      if (g) g.classList.remove('is-loading');
    }
    async loadMore(btn) {
      const next = btn.dataset.next;
      if (!next) return;
      btn.classList.add('is-loading');
      const u = new URL(next, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      try {
        const res = await fetch(u.toString());
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = doc.querySelectorAll('[data-collection-grid] > *');
        const grid = $('[data-collection-grid]', this);
        items.forEach((el) => grid.appendChild(el));
        const freshMore = doc.querySelector('[data-load-more-wrap]');
        const currentMore = $('[data-load-more-wrap]', this);
        if (currentMore) { if (freshMore) currentMore.replaceWith(freshMore); else currentMore.remove(); }
        initReveal(this);
      } catch (e) { SP.toast(strings.error); }
      btn.classList.remove('is-loading');
    }
    setDensity(btn) {
      const grid = $('[data-collection-grid]', this);
      if (!grid) return;
      const group = btn.closest('.density');
      $$('[data-density]', group).forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
      const [scope, cols] = btn.dataset.density.split(':');
      if (scope === 'd') grid.style.setProperty('--cols-desktop', cols);
      else grid.dataset.mobileCols = cols;
      try { localStorage.setItem('sp-density-' + scope, cols); } catch (e) {}
    }
    restoreDensity() {
      const grid = $('[data-collection-grid]', this);
      if (!grid) return;
      try {
        const d = localStorage.getItem('sp-density-d');
        const m = localStorage.getItem('sp-density-m');
        if (d) { grid.style.setProperty('--cols-desktop', d); $$('[data-density^="d:"]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.density === 'd:' + d))); }
        if (m) { grid.dataset.mobileCols = m; $$('[data-density^="m:"]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.density === 'm:' + m))); }
      } catch (e) {}
    }
  }
  customElements.define('sp-collection', SpCollection);

  /* ---------------- Reveal on scroll ---------------- */
  function initReveal(root = document) {
    if (!document.body.classList.contains('has-reveal')) return;
    const els = $$('[data-reveal]:not(.is-revealed)', root);
    if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-revealed')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-revealed'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  }
  SP.initReveal = initReveal;

  /* ---------------- Boot ---------------- */
  function boot() {
    overlay.init();
    if (window.Shopify && window.Shopify.designMode) document.documentElement.classList.add('shopify-design-mode');
    initReveal();
    $$('img[loading="lazy"]').forEach((img) => {
      if (img.complete) return;
      img.classList.add('is-loading');
      img.addEventListener('load', () => img.classList.remove('is-loading'), { once: true });
      img.addEventListener('error', () => img.classList.remove('is-loading'), { once: true });
    });
    if (SP.cartType === 'upcart' && typeof window.upcartSubscribeCartUpdated === 'function') {
      try { window.upcartSubscribeCartUpdated(() => SP.cart.refreshCount()); } catch (e) {}
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
})();
